import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import Tag, { TagVariant } from '@/components/molecules/tag/tag';
import styles from './tag-list.module.scss';

export interface TagListProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the tag list is side scrollable */
  hasSideScroll?: boolean;
  /** An array of the tags to show */
  tags: {
    text: string;
    variant: TagVariant;
  }[];
}

const TagList = ({
  className,
  hasSideScroll = false,
  tags = [],
}: TagListProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isSwiped, setIsSwiped] = useState(false);
  const [isSwipedToEnd, setIsSwipedToEnd] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    if (containerRef.current) {
      const { clientWidth, scrollLeft, scrollWidth } = containerRef.current;

      setIsSwiped(scrollLeft > 0);
      setIsSwipedToEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current;

        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.component, className, {
        [styles.hasMaskLeft]: isSwipedToEnd,
        [styles.hasMaskLeftAndRight]: isSwiped && !isSwipedToEnd,
        [styles.hasMaskRight]: isOverflowing && !isSwiped,
        [styles.hasSideScroll]: hasSideScroll,
      })}
    >
      {tags.map((tag, tagIndex) => (
        <Tag key={tagIndex} text={tag.text} variant={tag.variant} />
      ))}
    </div>
  );
};

TagList.displayName = 'TagList';

export default TagList;
